import moment from 'moment';
import React, { useEffect, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';
import { EfasConcepts } from '../../../models/EfasConcepts';
import { QCalificationInitials } from '../../../models/EsgQCalification';
import { IndicatorKeyEnum } from '../../../models/IndicatorKeyEnum';
import Project from '../../../models/Project';
import {
  setIndicatorDetailFilterChart,
  setIndicatorDetailFilterProjectType,
} from '../../../redux/actions/indicatorDetailFilters';
import {
  getProjectByESGCategory,
  getProjectsByEfasOpinion,
  getProjectsByPmr,
  getProjectsByCTPerfomanceRating,
} from '../../../redux/actions/indicatorProjects';
import { getDynamicTableFormat, parseFilterToApiCall } from '../../../services/util';
import { translateMultipleText } from '../../../services/stringUtil';
import { EfasPortfolio } from '../EfasPortfolio/EfasPortfolio';
import { EsgCategoryPortfolio } from '../EsgCategoryPortfolio/EsgCategoryPortfolio';
import { PmrPortfolio } from '../PmrPortfolio/PmrPortfolio';
import { mapEfaConceptClassification, mapClassification } from '../PortfolioDetailsUtil';
import { PortfolioProjectsTable } from '../PortfolioProjectsTable/PortfolioProjectsTable';
import { PortfolioSimpleIndicatorTable } from '../PortfolioSimpleIndicatorTable/PortfolioSimpleIndicatorTable';
import { ProjectCounter } from '../ProjectCounter/ProjectCounter';
import './WorkWithDoughnutChart.scss';
import { sortTablesByType } from '../../../services/sortsUtil';
import { AppMenuTabs } from '../../MenuTabs/userTabsInterface';
import { setIndicatorResponse, setLoading } from '../../../redux/actions/dataTable';
import { getNewFilteredIndicators } from '../../../api/indicatorApi';
import { emptyProjects } from '../../Portfolio/PortfolioUtil';
import { ProjectProductTypes } from '../../../models/ProjectProductTypes';
import { processIndicators } from '../../Portfolio/indicatorHelper';
import { arrayMove } from 'react-sortable-hoc';
import { addIndicators } from '../../../redux/actions/indicators';
import { updateCreatingOrEditing } from '../../../redux/actions/menuTabs';
import FilterIndicators from '../../../models/FilterIndicators';
import { setCurrentIndicator } from '../../../redux/actions/currentIndicator';
import { ProjectsListSkeleton } from '../../LoadersSkeletons/ProjectsListSkeleton/ProjectsListSkeleton';

type Props = {
  currentIndicator: any;
  conceptFilter: any;
  fiscalYearFilters?: any;
  loaderInfo;
  noData?: boolean;
};
enum indicatorType {
  EFAS = 'DISTRIBUTION_ACCORDING_TO_LATEST_EFAS',
  ESG = 'ESG_DISTRIBUTION_ACCORDING_TO_ENVIRONMENTAL_AND_SOCIAL_CATEGORY',
  PMR = 'PMR_STATE_DISTRIBUTION',
  TCP = 'TCP_PERFORMANCE',
}

export const WorkWithDoughnutChart = ({
  currentIndicator: { id: indicatorId, indicatorKey },
  conceptFilter,
  fiscalYearFilters,
  loaderInfo,
  noData = false,
}: Props) => {
  const dispatch = useDispatch();
  const { t } = useTranslation();
  const indicatorProjects = useSelector<any, Project[]>(state => state.indicatorProjects);
  const filterIndicators = useSelector<any, any>(state => state.indicatorDetailFilter);
  const [tableTitle, setTableTitle] = useState('');

  // filter
  const filterIndicatorsBar = useSelector<any, FilterIndicators>(state => state.filterIndicators);
  const stateProjectProductTypes = useSelector<any, FilterIndicators>(
    state => state.indicatorPayload.payload.projectProductTypes,
  );
  const loadingRedux = useSelector<any, boolean>(state => state.dataTableInfo.loading);
  const menuTabs = useSelector<any, AppMenuTabs>(state => state.menuTabs);
  const portfolioSelectedTab = menuTabs.portfolioSelectedTab;
  const creatingOrEditing = menuTabs.creatingOrEditing;
  const [isLoading, setIsLoading] = React.useState(false);
  const [totalProjects, setTotalProjects] = React.useState(false);

  const projectTypeFilterChange = {
    ...filterIndicatorsBar,
    common: { ...filterIndicatorsBar.common, projectTypeFilters: stateProjectProductTypes },
    projectTypeFilters: stateProjectProductTypes,
  };

  const handleCommonFilterChange = async (indicatorKey: string) => {
    setIsLoading(true);
    let indicatorsToProcess;

    try {
      if (indicatorKey === indicatorType.EFAS) {
        indicatorsToProcess = await getNewFilteredIndicators({}, parseFilterToApiCall([0, 1, 2, 3]), {
          ...filterIndicatorsBar.common,
          projectTypeFilters: [0, 1, 2, 3],
        });
      } else {
        indicatorsToProcess = await getNewFilteredIndicators(
          {},
          { projectProductTypes: stateProjectProductTypes },
          {
            ...projectTypeFilterChange.common,
          },
        );
      }

      dispatch(setIndicatorResponse(indicatorsToProcess));
      startProcessIndicators(
        indicatorsToProcess,
        translateMultipleText(` ${' '} ${portfolioSelectedTab.title}`, t),
        filterIndicatorsBar.common.projectTypeFilters,
        indicatorKey,
      );
      setTotalProjects(emptyProjects(indicatorsToProcess.totalProjects));
      dispatch(setLoading(true));
    } catch (err) {
      console.error(err, 'Error fetching data from indicators');
    } finally {
      setIsLoading(false);
      dispatch(setLoading(false));
    }
  };

  const startProcessIndicators = (inds, titleFromTab, projectTypeFilter = [ProjectProductTypes.Loan], indicatorKey) => {
    const result = processIndicators(inds, {
      titleFromTab,
      projectTypeFilter,
    });
    const elementSummary: any = result.find(elem => elem.id === 15);
    const indexSummary: number = result.indexOf(elementSummary);
    const sendEndSummary = arrayMove(result, indexSummary, result.length);

    dispatch(addIndicators(sendEndSummary));
    if (indicatorKey === indicatorType.EFAS) {
      dispatch(setIndicatorDetailFilterProjectType(0));
      dispatch(
        setCurrentIndicator(
          sendEndSummary?.find(data => data.indicatorKey === 'DISTRIBUTION_ACCORDING_TO_LATEST_EFAS'),
        ),
      );
    } else if (indicatorKey === indicatorType.ESG) {
      dispatch(
        setCurrentIndicator(
          sendEndSummary?.find(
            data => data.indicatorKey === 'ESG_DISTRIBUTION_ACCORDING_TO_ENVIRONMENTAL_AND_SOCIAL_CATEGORY',
          ),
        ),
      );
    } else if (indicatorKey === indicatorType.PMR) {
      dispatch(setCurrentIndicator(sendEndSummary?.find(data => data.indicatorKey === 'PMR_STATE_DISTRIBUTION')));
    } else if (indicatorKey === indicatorType.TCP) {
      dispatch(setCurrentIndicator(sendEndSummary?.find(data => data.indicatorKey === 'TCP_PERFORMANCE')));
    }
  };

  // const tabId0OrCreatingEditing = (menuTabs, creatingOrEditing) =>
  //   menuTabs.portfolioSelectedTab.tabId === 0 || creatingOrEditing;

  useEffect(() => {
    if (noData) {
      setTableTitle('');
      return;
    }

    if (conceptFilter.label !== '') {
      getFilteredProjects(
        dispatch,
        { indicatorId, indicatorKey, conceptFilter, fiscalYearFilters },
        projectTypeFilterChange,
      );
      setTableTitle(conceptFilter.label);
      dispatchIndicatorFilterChart(dispatch, filterIndicators, conceptFilter, indicatorKey);
      return;
    }

    if (
      (conceptFilter.label !== '' &&
        filterIndicators.indicatorCharts.firstTime &&
        loadingRedux &&
        indicatorKey === indicatorType.EFAS) ||
      (conceptFilter.label !== '' && filterIndicators.indicatorCharts.firstTime && indicatorKey === indicatorType.EFAS)
    ) {
      getFilteredProjects(
        dispatch,
        { indicatorId, indicatorKey, conceptFilter, fiscalYearFilters },
        projectTypeFilterChange,
      );
      setTableTitle(conceptFilter.label);
      dispatchIndicatorFilterChart(dispatch, filterIndicators, conceptFilter, indicatorKey);
    } else if (
      conceptFilter.label !== '' &&
      filterIndicators.indicatorCharts.firstTime &&
      indicatorKey === indicatorType.ESG
    ) {
      getFilteredProjects(
        dispatch,
        { indicatorId, indicatorKey, conceptFilter, fiscalYearFilters },
        projectTypeFilterChange,
      );
      setTableTitle(conceptFilter.label);
      dispatchIndicatorFilterChart(dispatch, filterIndicators, conceptFilter, indicatorKey);
    } else if (
      conceptFilter.label !== '' &&
      filterIndicators.indicatorCharts.firstTime &&
      indicatorKey === indicatorType.PMR
    ) {
      getFilteredProjects(
        dispatch,
        { indicatorId, indicatorKey, conceptFilter, fiscalYearFilters },
        projectTypeFilterChange,
      );
      setTableTitle(conceptFilter.label);
      dispatchIndicatorFilterChart(dispatch, filterIndicators, conceptFilter, indicatorKey);
    } else if (
      conceptFilter.label !== '' &&
      filterIndicators.indicatorCharts.firstTime &&
      indicatorKey === indicatorType.TCP
    ) {
      getFilteredProjects(
        dispatch,
        { indicatorId, indicatorKey, conceptFilter, fiscalYearFilters },
        projectTypeFilterChange,
      );
      setTableTitle(conceptFilter.label);
      dispatchIndicatorFilterChart(dispatch, filterIndicators, conceptFilter, indicatorKey);
    } else {
      const conceptFilter = filterIndicators.indicatorCharts.subTitle;
      getFilteredProjects(
        dispatch,
        { indicatorId, indicatorKey, conceptFilter, fiscalYearFilters },
        projectTypeFilterChange,
      );
      setTableTitle(conceptFilter.label);
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [conceptFilter, fiscalYearFilters, noData]);

  useEffect(() => {
    handleCommonFilterChange(indicatorKey);

    if (indicatorKey === indicatorType.EFAS) {
      dispatch(setIndicatorDetailFilterProjectType(1));
    }

    if (creatingOrEditing) {
      dispatch(updateCreatingOrEditing(false));
    }

    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [filterIndicatorsBar.common]);

  const projectsToRender = noData ? [] : indicatorProjects;

  return !loaderInfo && !isLoading ? (
    <div className="work-with-doughnut-chart-container">
      <div className="table-container">
        <div className='filtered-label'>
          <div className="title">{renderTitle(indicatorKey, t)}</div>
          <div>{renderSelectedTitle(indicatorKey, noData ? undefined : tableTitle)}</div>
        </div>
        {renderTableByIndicatorKey(indicatorKey, projectsToRender, conceptFilter.label)}
        <ProjectCounter arrayToCount={projectsToRender} countProjects={true} indicatorKey={indicatorKey} />
      </div>
    </div>
  ) : (
    <ProjectsListSkeleton />
  );
};

export const dispatchIndicatorFilterChart = (d, filterIndicators, conceptFilter, indicatorKey) => {
  d(
    setIndicatorDetailFilterChart({
      ...filterIndicators.indicatorCharts,
      chartTitle: indicatorKey,
      subTitle: conceptFilter,
      firstTime: false,
    }),
  );
};

export const getFilteredProjects = (d, indicatorInfo, projectTypeFilterChange?) => {
  let returnedDispatch;
  const lastYear = moment().year() - 1;
  if (
    indicatorInfo.indicatorKey === IndicatorKeyEnum.PMR_STATE_DISTRIBUTION ||
    indicatorInfo.indicatorKey === IndicatorKeyEnum.TCP_PERFORMANCE
  ) {
    const classificationValue = mapClassification(indicatorInfo.conceptFilter.label);

    returnedDispatch =
      indicatorInfo.indicatorKey === IndicatorKeyEnum.PMR_STATE_DISTRIBUTION
        ? d(
          getProjectsByPmr(
            classificationValue,
            indicatorInfo.conceptFilter.totalProjects,
            indicatorInfo.indicatorId,
            projectTypeFilterChange,
          ),
        )
        : d(
          getProjectsByCTPerfomanceRating(
            classificationValue,
            indicatorInfo.conceptFilter.totalProjects,
            indicatorInfo.indicatorId,
            projectTypeFilterChange,
          ),
        );
  } else if (indicatorInfo.indicatorKey === IndicatorKeyEnum.DISTRIBUTION_ACCORDING_TO_LATEST_EFAS) {
    returnedDispatch = d(
      getProjectsByEfasOpinion(
        mapEfaConceptClassification(EfasConcepts[indicatorInfo.conceptFilter.label]),
        indicatorInfo.conceptFilter.totalProjects,
        indicatorInfo.indicatorId,
        indicatorInfo.fiscalYearFilters && indicatorInfo.fiscalYearFilters.fiscalYear
          ? indicatorInfo.fiscalYearFilters.fiscalYear
          : lastYear,
        projectTypeFilterChange,
      ),
    );
  } else if (
    indicatorInfo.indicatorKey === IndicatorKeyEnum.ESG_DISTRIBUTION_ACCORDING_TO_ENVIRONMENTAL_AND_SOCIAL_CATEGORY
  ) {
    const esgClassification =
      indicatorInfo.conceptFilter.label === 'not_assigned'
        ? QCalificationInitials.Not_Assigned
        : QCalificationInitials[indicatorInfo.conceptFilter.label];

    if (esgClassification) {
      returnedDispatch = d(
        getProjectByESGCategory(
          esgClassification,
          indicatorInfo.conceptFilter.totalProjects,
          indicatorInfo.indicatorId,
          projectTypeFilterChange,
        ),
      );
    }
  }

  return returnedDispatch;
};

const renderTitle = (indicatorKey, t) => {
  switch (indicatorKey) {
    case IndicatorKeyEnum.DISTRIBUTION_ACCORDING_TO_LATEST_EFAS:
      return translateMultipleText('efa_opinion efas', t);
    case IndicatorKeyEnum.PMR_STATE_DISTRIBUTION:
      return t('pmr_initials');
    case IndicatorKeyEnum.ESG_DISTRIBUTION_ACCORDING_TO_ENVIRONMENTAL_AND_SOCIAL_CATEGORY:
      return t('category');

    default:
      return '';
  }
};

const renderSelectedTitle = (indicatorKey, label) => {
  switch (indicatorKey) {
    case IndicatorKeyEnum.DISTRIBUTION_ACCORDING_TO_LATEST_EFAS:
      return label ? <EfasPortfolio efasConcept={EfasConcepts[label]} /> : '';
    case IndicatorKeyEnum.PMR_STATE_DISTRIBUTION:
    case IndicatorKeyEnum.TCP_PERFORMANCE:
      return <PmrPortfolio pmrClassification={label !== undefined ? mapClassification(label) : undefined} indicatorKey={indicatorKey} />;
    case IndicatorKeyEnum.ESG_DISTRIBUTION_ACCORDING_TO_ENVIRONMENTAL_AND_SOCIAL_CATEGORY:
      return label ? <EsgCategoryPortfolio esgCategoryConcept={label} /> : '';

    default:
      return '';
  }
};

const renderTableByIndicatorKey = (indicatorKey, indicatorProjects, label) => {
  if (
    indicatorKey === IndicatorKeyEnum.DISTRIBUTION_ACCORDING_TO_LATEST_EFAS ||
    indicatorKey === IndicatorKeyEnum.ESG_DISTRIBUTION_ACCORDING_TO_ENVIRONMENTAL_AND_SOCIAL_CATEGORY
  ) {
    return (
      <PortfolioSimpleIndicatorTable
        projects={
          indicatorKey === IndicatorKeyEnum.DISTRIBUTION_ACCORDING_TO_LATEST_EFAS
            ? sortTablesByType(indicatorProjects, true, 'code', 'string')
            : indicatorProjects
        }
        tableInfo={getDynamicTableFormat(indicatorKey)}
        indicatorKey={indicatorKey}
        label={label}
      />
    );
  } else if (indicatorKey === IndicatorKeyEnum.TCP_PERFORMANCE) {
    return <PortfolioProjectsTable haveTcdi={true} projects={indicatorProjects} indicatorKey={indicatorKey} />;
  } else {
    return <PortfolioProjectsTable havePmr={true} projects={indicatorProjects} indicatorKey={indicatorKey} />;
  }
};
